import React, { useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FileText, ChevronDown, ChevronUp, Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { apiClient } from "../services/api";

const fmt = (val, currency = "TND") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency, minimumFractionDigits: 2 }).format(val ?? 0);

const periodLabel = (entry) => {
  if (entry.calculated_at) {
    return new Date(entry.calculated_at).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  }
  if (!entry.period_id) return `#${entry.id}`;
  return `P-${entry.period_id}`;
};

const EntryRow = ({ entry, currency }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const statusKey = entry.approval_status;

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-accent transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-center gap-3">
          <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
          <div>
            <p className="font-medium text-sm">{periodLabel(entry)}</p>
            <p className="text-xs text-muted-foreground">
              {t("portal.payslips.calculatedOn")} {new Date(entry.calculated_at).toLocaleDateString(i18n.language)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-xs">
            {t(`portal.payslips.status.${statusKey}`, statusKey)}
          </Badge>
          <span className="font-semibold text-sm tabular-nums">{fmt(entry.net_pay, currency)}</span>
          {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </div>
      </button>

      {open && (
        <div className="border-t bg-muted/30 px-4 py-3 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground mb-1">{t("portal.payslips.grossSalary")}</p>
              <p className="font-medium">{fmt(entry.gross_pay, currency)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">{t("portal.payslips.deductions")}</p>
              <p className="font-medium text-red-600">
                <TrendingDown className="w-3 h-3 inline mr-1" />
                {fmt(entry.total_deductions, currency)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">{t("portal.payslips.netSalary")}</p>
              <p className="font-semibold text-green-400">
                <TrendingUp className="w-3 h-3 inline mr-1" />
                {fmt(entry.net_pay, currency)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">{t("portal.payslips.daysWorked")}</p>
              <p className="font-medium">{entry.total_hours_worked ? `${entry.total_hours_worked}h` : "—"}</p>
            </div>
          </div>

          {(entry.fixed_allowances?.length > 0 || entry.variable_deductions?.length > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t text-xs">
              {entry.fixed_allowances?.length > 0 && (
                <div>
                  <p className="font-medium text-muted-foreground mb-1">{t("portal.payslips.allowances")}</p>
                  <ul className="space-y-0.5">
                    {entry.fixed_allowances.map((a, i) => (
                      <li key={i} className="flex justify-between">
                        <span>{a.name}</span>
                        <span className="text-green-400">+{fmt(a.amount, currency)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {entry.variable_deductions?.length > 0 && (
                <div>
                  <p className="font-medium text-muted-foreground mb-1">{t("portal.payslips.variableDeductions")}</p>
                  <ul className="space-y-0.5">
                    {entry.variable_deductions.map((d, i) => (
                      <li key={i} className="flex justify-between">
                        <span>{d.name}</span>
                        <span className="text-red-600">-{fmt(d.amount, currency)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MesBulletins = () => {
  const { t } = useTranslation();

  const [entriesQ, companyQ] = useQueries({
    queries: [
      {
        queryKey: ['my-payroll-entries'],
        queryFn: () => apiClient.get("v1/payroll/my-entries?limit=24").then(r => r.data.items ?? []),
      },
      {
        queryKey: ['my-company-info'],
        queryFn: async () => {
          const r = await apiClient.get("v1/companies/");
          return r.data?.company?.payroll_currency ?? "TND";
        },
      },
    ],
  });

  const entries = entriesQ.data ?? [];
  const currency = companyQ.data ?? "TND";
  const loading = entriesQ.isPending;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
          <FileText className="w-10 h-10 text-muted-foreground" />
          <p className="font-medium">{t("portal.payslips.noPayslips")}</p>
          <p className="text-sm text-muted-foreground">{t("portal.payslips.noPayslipsDesc")}</p>
        </CardContent>
      </Card>
    );
  }

  const totalNet = entries.reduce((s, e) => s + Number(e.net_pay ?? 0), 0);
  const latest = entries[0];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">{t("portal.payslips.lastNet")}</p>
            <p className="text-2xl font-bold mt-1">{fmt(latest?.net_pay, currency)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">{t("portal.payslips.lastGross")}</p>
            <p className="text-2xl font-bold mt-1">{fmt(latest?.gross_pay, currency)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">{t("portal.payslips.totalNet")} ({entries.length})</p>
            <p className="text-2xl font-bold mt-1">{fmt(totalNet, currency)}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("portal.payslips.history")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {entries.map((entry) => (
            <EntryRow key={entry.id} entry={entry} currency={currency} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default MesBulletins;
